import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as numberingApi from './api'
import type { CreateAssignmentRequest, CreateNumberingSchemeRequest, UpdateNumberingSchemeRequest } from './types'

export const numberingKeys = {
  schemes: () => ['admin', 'numbering', 'schemes'] as const,
  assignments: () => ['admin', 'numbering', 'assignments'] as const,
}

// ---- Scheme queries ----

export function useNumberingSchemes() {
  return useQuery({
    queryKey: numberingKeys.schemes(),
    queryFn: numberingApi.listSchemes,
  })
}

// ---- Scheme mutations ----

function invalidateSchemes(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: numberingKeys.schemes() })
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  invalidateSchemes(qc)
  qc.invalidateQueries({ queryKey: numberingKeys.assignments() })
}

export function useCreateScheme() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateNumberingSchemeRequest) => numberingApi.createScheme(payload),
    onSuccess: () => invalidateSchemes(qc),
  })
}

export function useUpdateScheme() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { code: string; payload: UpdateNumberingSchemeRequest }) =>
      numberingApi.updateScheme(vars.code, vars.payload),
    onSuccess: () => invalidateSchemes(qc),
  })
}

export function useDeactivateScheme() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => numberingApi.deactivateScheme(code),
    onSuccess: () => invalidateSchemes(qc),
  })
}

export function useResetSchemeCounter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => numberingApi.resetSchemeCounter(code),
    onSuccess: () => invalidateSchemes(qc),
  })
}

// ---- Assignment queries ----

export function useNumberingAssignments() {
  return useQuery({
    queryKey: numberingKeys.assignments(),
    queryFn: numberingApi.listAssignments,
  })
}

// ---- Assignment mutations ----

export function useCreateAssignment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateAssignmentRequest) => numberingApi.createAssignment(payload),
    onSuccess: () => invalidateAll(qc),
  })
}

export function useDeleteAssignment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => numberingApi.deleteAssignment(id),
    onSuccess: () => invalidateAll(qc),
  })
}
